import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useServiceOfferings from '../../hooks/useServiceOfferings';
import olreadiaService from '../../services/olreadiaService';
import { SERVICE_CATEGORIES, formatEnumLabel } from '../../services/businessConstants';
import '../../styles/ServiceOfferingsPage.css';

interface ServiceOfferingsPageProps {
  businessEntityId: string;
}

const getOfferingRecord = (record: any) =>
  record?.serviceOffering || record?.businessOffering || record?.offering || record?.service || record;

const getOfferingId = (record: any) =>
  record?.id || record?.serviceOfferingId || record?.serviceId || record?.businessOfferingId;

const ServiceOfferingsPage: React.FC<ServiceOfferingsPageProps> = ({ businessEntityId }) => {
  const { user } = useAuth();
  const { offerings, loading: offeringsLoading, list, create, update } = useServiceOfferings();
  const [businessServices, setBusinessServices] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [editingService, setEditingService] = useState<any>(null);
  const [selectedOfferingId, setSelectedOfferingId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadServices();
  }, [businessEntityId]);

  const loadServices = async () => {
    try {
      const [, resp] = await Promise.all([
        list({ page: 0, size: 500 }),
        olreadiaService.serviceOfferings.businessEntityServices.list(businessEntityId),
      ]);
      const content = Array.isArray(resp) ? resp : Array.isArray(resp?.content) ? resp.content : [];
      setBusinessServices(content.map(getOfferingRecord).filter((offering) => Boolean(getOfferingId(offering))));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load services.' });
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowForm(true);
  };

  const handleSaveService = async (serviceData: any) => {
    if (!user?.id) return;
    setSaving(true);
    setMessage(null);

    try {
      if (editingService?.id) {
        await update(editingService.id, serviceData, user.id);
        setMessage({ type: 'success', text: 'Service updated.' });
      } else {
        const created = await create(serviceData, user.id);
        if (created?.id) {
          await olreadiaService.serviceOfferings.businessEntityServices.add(businessEntityId, created.id, user.id);
        }
        setMessage({ type: 'success', text: 'Service created and attached.' });
      }
      setShowForm(false);
      setEditingService(null);
      await loadServices();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save service.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAttachService = async (serviceId: string) => {
    if (!user?.id) return;
    setSaving(true);

    try {
      await olreadiaService.serviceOfferings.businessEntityServices.add(businessEntityId, serviceId, user.id);
      setMessage({ type: 'success', text: 'Service attached.' });
      setSelectedOfferingId('');
      setShowAttach(false);
      await loadServices();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to attach service.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDetachService = async (serviceId: string) => {
    if (!user?.id || !window.confirm('Remove service?')) return;
    setSaving(true);

    try {
      await olreadiaService.serviceOfferings.businessEntityServices.remove(businessEntityId, serviceId, user.id);
      setMessage({ type: 'success', text: 'Service removed.' });
      await loadServices();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to remove service.' });
    } finally {
      setSaving(false);
    }
  };

  const businessServiceIds = new Set(businessServices.map(getOfferingId).filter(Boolean));
  const attachableOfferings = offerings.filter((offering) => offering?.id && !businessServiceIds.has(offering.id));
  const filteredBusinessServices = businessServices.filter((svc) => {
    const q = filter.toLowerCase();
    return (
      svc.code?.toLowerCase().includes(q) ||
      svc.name?.toLowerCase().includes(q) ||
      svc.category?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="service-offerings-page">
      {message && <div className={`service-message ${message.type}`}>{message.text}</div>}

      <div className="services-header">
        <h2>Business Offerings</h2>
        <div className="service-header-actions">
          <button className="btn-add-service" onClick={() => setShowAttach((value) => !value)} disabled={offeringsLoading || saving}>
            {showAttach ? 'Close Attach' : 'Attach Offering'}
          </button>
          <button className="btn-add-service" onClick={handleAddService} disabled={offeringsLoading || saving}>
            Create Offering
          </button>
        </div>
      </div>

      {showForm ? (
        <div className="service-form-container">
          <button className="btn-close-form" onClick={() => setShowForm(false)}>← Back</button>
          <ServiceFormComponent
            initialValues={editingService}
            onSubmit={handleSaveService}
            loading={saving}
          />
        </div>
      ) : (
        <div className="services-list">
          {showAttach && (
            <div className="service-form attach-service-form">
              <div className="form-group">
                <label>Select Service Offering</label>
                <select value={selectedOfferingId} onChange={(e) => setSelectedOfferingId(e.target.value)} disabled={saving}>
                  <option value="">Select offering</option>
                  {attachableOfferings.map((offering) => (
                    <option key={offering.id} value={offering.id}>
                      {offering.name} {offering.category ? `(${formatEnumLabel(offering.category)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="btn-save"
                type="button"
                disabled={!selectedOfferingId || saving}
                onClick={() => handleAttachService(selectedOfferingId)}
              >
                {saving ? 'Attaching...' : 'Attach Offering'}
              </button>
              {attachableOfferings.length === 0 && (
                <p className="no-services">All available offerings are already attached to this business.</p>
              )}
            </div>
          )}

          <input
            type="text"
            className="search-filter"
            placeholder="Search attached offerings by code, name, or category..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

          {offeringsLoading ? (
            <p>Loading business offerings...</p>
          ) : filteredBusinessServices.length === 0 ? (
            <p className="no-services">No offerings attached to this business.</p>
          ) : (
            <div className="services-table">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinessServices.map((svc) => (
                    <tr key={getOfferingId(svc)}>
                      <td>{svc.code}</td>
                      <td>{svc.name}</td>
                      <td>{formatEnumLabel(svc.category || '')}</td>
                      <td>
                        <span className={`status-badge status-${svc.active ? 'active' : 'inactive'}`}>
                          {svc.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="btn-detach" onClick={() => handleDetachService(getOfferingId(svc))} disabled={saving}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ServiceFormComponentProps {
  initialValues?: any;
  onSubmit: (data: any) => Promise<void>;
  loading: boolean;
}

const ServiceFormComponent: React.FC<ServiceFormComponentProps> = ({ initialValues, onSubmit, loading }) => {
  const [formData, setFormData] = React.useState({
    code: '',
    name: '',
    description: '',
    category: '',
    active: true,
  });
  const [formMessage, setFormMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

  React.useEffect(() => {
    if (initialValues) {
      setFormData({
        code: initialValues.code || '',
        name: initialValues.name || '',
        description: initialValues.description || '',
        category: initialValues.category || '',
        active: initialValues.active !== false,
      });
    }
  }, [initialValues]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormMessage(null);

    if (!formData.code.trim()) {
      setFormMessage({ type: 'error', text: 'Service code is required.' });
      return;
    }

    if (!formData.name.trim()) {
      setFormMessage({ type: 'error', text: 'Service name is required.' });
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error: any) {
      setFormMessage({ type: 'error', text: error?.message || 'Failed to save service.' });
    }
  };

  return (
    <form className="service-form" onSubmit={handleSubmit}>
      {formMessage && <div className={`form-message ${formMessage.type}`}>{formMessage.text}</div>}

      <div className="form-group">
        <label>Service Code *</label>
        <input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
          placeholder="e.g., HOME_CARE_001"
          required
        />
      </div>

      <div className="form-group">
        <label>Service Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
          placeholder="e.g., Home Care Service"
          required
        />
      </div>

      <div className="form-group">
        <label>Category</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
        >
          <option value="">Select category</option>
          {SERVICE_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{formatEnumLabel(cat)}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
          placeholder="Service description"
        />
      </div>

      <div className="form-group checkbox">
        <label>
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))}
          />
          Active
        </label>
      </div>

      <button type="submit" className="btn-save" disabled={loading}>
        {loading ? 'Saving...' : 'Save Service'}
      </button>
    </form>
  );
};

export default ServiceOfferingsPage;
